import React, { useContext, useState, useEffect } from 'react';
import { FBAInboundContext } from '../FBAInboundContext';
import * as api from '../services/fbaInboundApi';
import { FileText, Plus, ArrowRight, Loader2, AlertTriangle, CheckCircle, XCircle, Clock, Package, Trash2 } from 'lucide-react';

const Step0_PlanSelector = () => {
  const {
    outboundId,
    availablePlans,
    loadAvailablePlans,
    selectExistingPlan,
    createNewPlan,
    loading,
    error,
    planId,
    resetToStep0
  } = useContext(FBAInboundContext);

  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isSelecting, setIsSelecting] = useState(null);
  const [isVoiding, setIsVoiding] = useState(null); // plan id being voided
  const [isVoidingAll, setIsVoidingAll] = useState(false);

  useEffect(() => {
    const loadPlans = async () => {
      setIsLoading(true);
      try {
        await loadAvailablePlans();
      } catch (err) {
        console.error('Failed to load plans:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (outboundId) {
      loadPlans();
    }
  }, [outboundId, loadAvailablePlans]);

  const handleSelectPlan = async (plan) => {
    setIsSelecting(plan.id);
    try {
      await selectExistingPlan(plan);
    } catch (err) {
      console.error('Failed to select plan:', err);
    } finally {
      setIsSelecting(null);
    }
  };

  const handleCreateNew = async () => {
    setIsCreating(true);
    try {
      await createNewPlan();
    } catch (err) {
      console.error('Failed to create new plan:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleVoidPlan = async (plan, e) => {
    e.stopPropagation(); // Prevent triggering parent click
    setIsVoiding(plan.id);
    try {
      console.log(`Voiding plan ${plan.id}...`);
      await api.deletePlan(plan.id);
      // Reload the plans list
      await loadAvailablePlans();
      // Reset workflow state if we just voided the active plan
      if (plan.id === planId) {
        resetToStep0();
      }
    } catch (err) {
      console.error('Failed to void plan:', err);
    } finally {
      setIsVoiding(null);
    }
  };

  const handleVoidAllPlans = async () => {
    setIsVoidingAll(true);
    try {
      const planIds = activePlans.filter(p => !p.is_canceled).map(p => p.id);
      console.log(`Batch voiding ${planIds.length} plans...`);

      // Use batch cancel endpoint for parallel processing
      const result = await api.batchCancelPlans(planIds);
      console.log('Batch cancel result:', result);

      // Reload the plans list
      await loadAvailablePlans();

      // Reset workflow state since we voided all plans
      resetToStep0();
    } catch (err) {
      console.error('Failed to void all plans:', err);
    } finally {
      setIsVoidingAll(false);
    }
  };

  const getStatusBadge = (plan) => {
    if (plan.is_canceled) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="w-3 h-3 mr-1" />
          Voided
        </span>
      );
    }
    if (plan.is_locked) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3 mr-1" />
          Locked
        </span>
      );
    }
    if (plan.placement_options?.some(p => p.is_selected)) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
          <Package className="w-3 h-3 mr-1" />
          In Progress
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
        <Clock className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  const getProgressText = (plan) => {
    if (plan.is_locked) return 'Ready for labels';
    if (plan.placement_options?.some(p => p.is_selected)) return 'Placement selected';
    if (plan.placement_options?.length > 0) return 'Placement options generated';
    if (plan.packing_options?.some(p => p.confirmed_at)) return 'Packing confirmed';
    if (plan.amazon_plan_id) return 'Plan created';
    return 'Started';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if there's an active (non-voided) plan
  const activePlans = availablePlans?.filter(p => !p.is_canceled) || [];
  const voidedPlans = availablePlans?.filter(p => p.is_canceled) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading plans...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <FileText className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-blue-900">FBA Inbound Plans</h4>
            <p className="text-sm text-blue-700 mt-1">
              Select an existing plan to continue or create a new one.
              {activePlans.length > 0 && ' Creating a new plan will void any existing active plans.'}
            </p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Active Plans */}
      {activePlans.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">Active Plans ({activePlans.length})</h3>
            <button
              onClick={handleVoidAllPlans}
              disabled={isVoidingAll || loading}
              className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isVoidingAll ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  Voiding...
                </>
              ) : (
                <>
                  <Trash2 className="w-3 h-3 mr-1.5" />
                  Void All
                </>
              )}
            </button>
          </div>
          {activePlans.map((plan) => (
            <div
              key={plan.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="font-medium text-gray-900">
                      Plan #{plan.id}
                    </span>
                    {getStatusBadge(plan)}
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-gray-600">
                    <p>Created: {formatDate(plan.created_at)}</p>
                    {plan.amazon_plan_id && (
                      <p className="text-xs text-gray-500">Amazon ID: {plan.amazon_plan_id}</p>
                    )}
                    <p className="text-blue-600 font-medium">{getProgressText(plan)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => handleVoidPlan(plan, e)}
                    disabled={isVoiding === plan.id || loading}
                    className="px-3 py-2 text-red-600 bg-red-50 border border-red-200 text-sm font-medium rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    title="Void this plan"
                  >
                    {isVoiding === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isSelecting === plan.id || loading}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSelecting === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create New Plan Button */}
      <div className="border-t border-gray-200 pt-6">
        <button
          onClick={handleCreateNew}
          disabled={isCreating || loading}
          className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isCreating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {activePlans.length > 0 ? 'Voiding existing plan and starting fresh...' : 'Starting...'}
            </>
          ) : (
            <>
              <Plus className="w-5 h-5 mr-2" />
              {activePlans.length > 0 ? 'Create New Plan (Voids Existing)' : 'Create New Plan'}
            </>
          )}
        </button>
      </div>

      {/* Voided Plans (Collapsed) */}
      {voidedPlans.length > 0 && (
        <details className="border border-gray-200 rounded-lg">
          <summary className="px-4 py-3 cursor-pointer text-sm text-gray-600 hover:text-gray-800">
            Show voided plans ({voidedPlans.length})
          </summary>
          <div className="px-4 pb-4 space-y-2">
            {voidedPlans.map((plan) => (
              <div
                key={plan.id}
                className="border border-gray-100 rounded-lg p-3 bg-gray-50 opacity-60"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Plan #{plan.id}
                    </span>
                    {getStatusBadge(plan)}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDate(plan.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* No Plans */}
      {availablePlans?.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No existing plans for this shipment.</p>
          <p className="text-sm mt-1">Click "Create New Plan" to get started.</p>
        </div>
      )}
    </div>
  );
};

export default Step0_PlanSelector;
